export interface CartProduct {
  uid: number;
  title: string;
  category: string;
  // Download file name (e.g. "74810903.zip"); carried into the checkout XML.
  file?: string;
}

export const SOFTWARE_CART_CHANGE_EVENT = "softwarecart:change";
export const SOFTWARE_CART_MAX_ITEMS = 10;

const STORAGE_KEY = "jh_software_cart";

export interface SoftwareCartChangeDetail {
  items: CartProduct[];
}

declare global {
  interface DocumentEventMap {
    [SOFTWARE_CART_CHANGE_EVENT]: CustomEvent<SoftwareCartChangeDetail>;
  }
}

class SoftwareCart {
  private items: CartProduct[];

  constructor() {
    this.items = SoftwareCart.load();
  }

  private static load(): CartProduct[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as CartProduct[];
    } catch {
      return [];
    }
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
  }

  private dispatch(): void {
    document.dispatchEvent(
      new CustomEvent<SoftwareCartChangeDetail>(SOFTWARE_CART_CHANGE_EVENT, {
        detail: { items: [...this.items] },
      })
    );
  }

  getItems(): CartProduct[] {
    return [...this.items];
  }

  getCount(): number {
    return this.items.length;
  }

  has(uid: number): boolean {
    return this.items.some((item) => item.uid === uid);
  }

  add(product: CartProduct): "added" | "duplicate" | "limit_reached" {
    if (this.has(product.uid)) return "duplicate";
    if (this.items.length >= SOFTWARE_CART_MAX_ITEMS) return "limit_reached";
    this.items.push(product);
    this.persist();
    this.dispatch();
    return "added";
  }

  remove(uid: number): void {
    const index = this.items.findIndex((item) => item.uid === uid);
    if (index === -1) return;
    this.items.splice(index, 1);
    this.persist();
    this.dispatch();
  }

  toggle(product: CartProduct): "added" | "removed" | "limit_reached" {
    if (this.has(product.uid)) {
      this.remove(product.uid);
      return "removed";
    }
    const result = this.add(product);
    return result === "added" ? "added" : "limit_reached";
  }

  clear(): void {
    this.items = [];
    this.persist();
    this.dispatch();
  }
}

const softwareCart = new SoftwareCart();
export default softwareCart;
